/**
 * useCrmEntityProps — hook that ensures a CRM entity is in the store,
 * subscribes to it, and returns its properties as a flat Record.
 *
 * **Self-sufficient**: if the entity is not in the store (or only has
 * incomplete data from a WS event), this hook triggers a fetch via the
 * entity fetch batcher. Multiple components mounting simultaneously
 * batch their requests into a single API call.
 *
 * **Store-first**: the returned data always comes from the store, so
 * WebSocket property updates propagate automatically — no refetching
 * or cache invalidation needed.
 *
 * **Association-aware**: for known association types (e.g. contact →
 * company), the hook also subscribes to the associated entity and
 * injects derived properties (e.g. ahoy_company_name) into the flat
 * record. This lets content components access association-derived data
 * without knowing about the CRM association model.
 *
 * Returns undefined when:
 * - entityId is undefined (entity doesn't exist in CRM yet)
 * - The entity hasn't been fetched yet (loading state)
 *
 * Callers should fall back to enrichedData.current_values or payload
 * properties when this returns undefined.
 *
 * Usage:
 *   const crmProps = useCrmEntityProps(orgId, entityId);
 *   const flatProps = crmProps ?? extractProperties(payloadProperties);
 *   <ContentComponent flatProps={flatProps} enrichedData={enrichedData} />
 */

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { hasCompleteObject, subscribeToObject } from '@/lib/crm/store';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { ensureEntity } from './entityFetchBatcher';

// ─── Association → derived property mapping ─────────────────────────────────

/**
 * Maps association type names to the flat property key that should be
 * populated from the associated entity's display name.
 *
 * When a CRM entity has associations, we subscribe to the target entity
 * in the store and inject its display name as a derived flat property.
 */
const ASSOCIATION_DERIVED_PROPS: Record<string, string> = {
  ahoy_contact_to_company: 'ahoy_company_name',
};

// ─── Property extraction ────────────────────────────────────────────────────

/**
 * Extract all properties from a CrmObject into a flat Record, matching
 * the shape that `extractProperties` produces from action payloads.
 *
 * e.g. { ahoy_email: "alice@example.com", ahoy_job_title: "CTO", ... }
 */
function extractFlatPropsFromCrmObject(
  obj: CrmObject
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (!obj.properties) {
    return result;
  }
  for (const [key, propValue] of Object.entries(obj.properties)) {
    let value = getPropertyValue(propValue);
    if (value === undefined || value === null) {
      continue;
    }
    // Unwrap single-element arrays — CRM stores many scalar properties
    // (email, job title, etc.) as arrays. Downstream formatValue/display
    // helpers expect scalar strings, not arrays.
    if (Array.isArray(value) && value.length === 1) {
      value = value[0];
    }
    result[key] = value;
  }
  return result;
}

/**
 * Find the first association on a CrmObject that maps to a derived property.
 * Returns the target entity ID, or undefined if no matching association exists.
 */
function findDerivedAssociationTarget(
  obj: CrmObject | undefined
): string | undefined {
  if (!obj?.associations) {
    return undefined;
  }
  for (const assoc of obj.associations) {
    if (assoc.association_type_name in ASSOCIATION_DERIVED_PROPS) {
      return assoc.target_entity_id;
    }
  }
  return undefined;
}

/**
 * Build derived properties from a CrmObject's associations and the
 * resolved associated entity.
 */
function buildDerivedAssociationProps(
  primaryObj: CrmObject | undefined,
  associatedObj: CrmObject | undefined
): Record<string, string> {
  const result: Record<string, string> = {};
  if (!primaryObj?.associations || !associatedObj) {
    return result;
  }
  for (const assoc of primaryObj.associations) {
    const derivedKey = ASSOCIATION_DERIVED_PROPS[assoc.association_type_name];
    if (derivedKey) {
      const name = getDisplayName(associatedObj, '');
      if (name) {
        result[derivedKey] = name;
      }
    }
  }
  return result;
}

// ─── Stable fallbacks for conditional subscription ──────────────────────────

const NOOP_SUBSCRIBE = (_cb: () => void) => () => {};

// ─── Hook ───────────────────────────────────────────────────────────────────

/**
 * Subscribe to the CRM object store and return flat property values
 * for the given entity. Triggers a batch fetch if the entity is not
 * already complete in the store.
 *
 * @param orgId - The org ID for the API call (needed for fetch)
 * @param entityId - The entity UUID to subscribe to
 * @returns Flat property map, or undefined if not yet loaded / entity doesn't exist in CRM
 */
export function useCrmEntityProps(
  orgId: number,
  entityId: string | undefined
): Record<string, unknown> | undefined {
  // ── Phase 1: Subscribe to the primary entity ────────────────────────────

  // Trigger fetch if entity is not complete in the store.
  // ensureEntity is idempotent — no-ops if already complete or in-flight.
  useEffect(() => {
    if (entityId && orgId > 0 && !hasCompleteObject(entityId)) {
      ensureEntity(orgId, entityId);
    }
  }, [orgId, entityId]);

  const sub = useMemo(
    () => (entityId ? subscribeToObject(entityId) : null),
    [entityId]
  );

  const subscribe = useCallback(
    (cb: () => void) => (sub ? sub.subscribe(cb) : NOOP_SUBSCRIBE(cb)),
    [sub]
  );
  const getSnapshot = useCallback(
    () => (sub ? sub.getSnapshot() : undefined),
    [sub]
  );

  const storeObject = useSyncExternalStore(subscribe, getSnapshot);

  // ── Phase 2: Subscribe to derived association target ─────────────────────
  // e.g. a contact's company (ahoy_contact_to_company) — we subscribe so
  // the component re-renders when the associated entity arrives in the store.

  const derivedTargetId = useMemo(
    () => findDerivedAssociationTarget(storeObject),
    [storeObject]
  );

  // Ensure the associated entity is fetched
  useEffect(() => {
    if (derivedTargetId && orgId > 0 && !hasCompleteObject(derivedTargetId)) {
      ensureEntity(orgId, derivedTargetId);
    }
  }, [orgId, derivedTargetId]);

  const derivedSub = useMemo(
    () => (derivedTargetId ? subscribeToObject(derivedTargetId) : null),
    [derivedTargetId]
  );

  const derivedSubscribe = useCallback(
    (cb: () => void) =>
      derivedSub ? derivedSub.subscribe(cb) : NOOP_SUBSCRIBE(cb),
    [derivedSub]
  );
  const derivedGetSnapshot = useCallback(
    () => (derivedSub ? derivedSub.getSnapshot() : undefined),
    [derivedSub]
  );

  const derivedObject = useSyncExternalStore(
    derivedSubscribe,
    derivedGetSnapshot
  );

  // ── Phase 3: Merge properties + derived association props ───────────────

  return useMemo(() => {
    if (!storeObject) {
      return undefined;
    }
    const baseProps = extractFlatPropsFromCrmObject(storeObject);
    const derivedProps = buildDerivedAssociationProps(
      storeObject,
      derivedObject
    );
    // Derived props fill gaps — don't overwrite existing properties
    return { ...derivedProps, ...baseProps };
  }, [storeObject, derivedObject]);
}
